import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
const filterChips = ["ALL", "SECURITY", "PRIVACY", "SPEED", "UPDATES"];
const coverPalettes = [{
  bg: "bg-primary",
  text: "text-primary-foreground",
  rule: "bg-secondary",
  accent: "text-secondary"
}, {
  bg: "bg-secondary",
  text: "text-secondary-foreground",
  rule: "bg-primary",
  accent: "text-primary"
}, {
  bg: "bg-card",
  text: "text-card-foreground",
  rule: "bg-primary",
  accent: "text-primary"
}, {
  bg: "bg-primary",
  text: "text-primary-foreground",
  rule: "bg-secondary",
  accent: "text-secondary"
}, {
  bg: "bg-secondary",
  text: "text-secondary-foreground",
  rule: "bg-primary",
  accent: "text-primary"
}, {
  bg: "bg-card",
  text: "text-card-foreground",
  rule: "bg-primary",
  accent: "text-primary"
}];
function stripHtml(html?: string): string {
  if (!html) return "";
  return html.replace(/<[^>]+>/g, "").replace(/\s+/g, " ").trim();
}
function formatDate(dateStr?: string): string {
  if (!dateStr) return "";
  try {
    const d = new Date(dateStr);
    return d.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "2-digit"
    }).toUpperCase();
  } catch {
    return "";
  }
}
function estimateReadingTime(content?: string, excerpt?: string): string {
  const text = stripHtml(content) || stripHtml(excerpt) || "";
  const words = text.split(/\s+/).filter(Boolean).length;
  const minutes = Math.max(3, Math.ceil(words / 200));
  return `${minutes} MIN READ`;
}
function RecentChaptersGrid() {
  const {
    posts,
    loading,
    error,
    refetch
  } = usePosts();
  if (loading) {
    return <div data-section-id="1156" className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-10">
        {[...Array(6)].map((_, i) => <div key={i} data-index={i} className="aspect-[3/4] bg-muted border border-border animate-pulse" />)}
      </div>;
  }
  if (error) {
    return <div className="border border-border bg-card p-10 text-center">
        <p className="font-sans italic text-foreground/80 mb-4">
          The feed is currently unavailable.
        </p>
        <Button onClick={refetch} variant="outline" className="font-mono uppercase tracking-[0.16em] text-xs rounded-md border-foreground text-foreground hover:bg-foreground hover:text-background">
          Retry
        </Button>
      </div>;
  }
  if (!posts || posts.length === 0) {
    return <div className="border border-border bg-card p-16 text-center">
        <p className="font-mono uppercase tracking-[0.18em] text-xs text-primary mb-3">
          § V.000 / VPN — NO POSTS
        </p>
        <p className="font-sans italic text-lg text-foreground/80">
          No articles found. Check back later.
        </p>
      </div>;
  }
  return <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-10">
      {posts.map((post, i) => {
      const palette = coverPalettes[i % coverPalettes.length];
      const category = post.categories && post.categories.length > 0 ? post.categories[0].name : "NEWS";
      const indexLabel = `V.${String(i + 1).padStart(3, "0")}${String.fromCharCode(97 + i % 3)}`;
      const editionNo = `No. ${String(i + 1).padStart(2, "0")}`;
      const cleanTitle = stripHtml(post.title);
      return <article data-index={i} key={post.id} className="group bg-card border border-border rounded-md overflow-hidden transition-all duration-300 ease-out hover:-translate-y-1 hover:shadow-lg">
            <Link to={post.link || "#"} className="block">
              {}
              <div className={`relative aspect-[3/4] ${palette.bg} ${palette.text} overflow-hidden transition-transform duration-300 ease-out group-hover:-rotate-1`}>
                {}
                <div className="absolute inset-0 opacity-[0.08] pointer-events-none mix-blend-overlay" style={{
              backgroundImage: "radial-gradient(circle at 20% 30%, hsl(var(--foreground)) 0%, transparent 2%), radial-gradient(circle at 70% 60%, hsl(var(--foreground)) 0%, transparent 1.5%), radial-gradient(circle at 40% 80%, hsl(var(--foreground)) 0%, transparent 2%)"
            }} />

                {}
                <div className="absolute top-6 left-6 right-6 flex items-start justify-between">
                  <span className={`font-mono text-[0.7rem] uppercase tracking-[0.18em] ${palette.accent}`}>
                    § {indexLabel}
                  </span>
                  <span className={`font-mono text-[0.65rem] uppercase tracking-[0.16em] ${palette.text} opacity-70`}>
                    {category}
                  </span>
                </div>

                {}
                <div className="absolute inset-x-6 top-1/2 -translate-y-1/2">
                  <h3 className="font-sans font-normal leading-[1.02] tracking-[-0.02em]" style={{
                fontSize: "clamp(1.5rem, 2.4vw, 2.125rem)"
              }}>
                    {cleanTitle}
                  </h3>
                  {}
                  <div className="mt-6 flex items-center">
                    <span className={`h-px w-16 ${palette.rule} transition-all duration-300 ease-out group-hover:w-32`} />
                    <span className={`ml-2 h-1.5 w-1.5 rounded-lg ${palette.rule}`} />
                  </div>
                </div>

                {}
                <div className="absolute bottom-6 left-6 right-6 flex items-end justify-between">
                  <span className={`font-mono text-[0.65rem] uppercase tracking-[0.18em] ${palette.text} opacity-80`}>
                    VPNGENIX
                  </span>
                  <span className={`font-mono text-[0.65rem] uppercase tracking-[0.16em] ${palette.accent}`}>
                    {editionNo}
                  </span>
                </div>
              </div>

              {}
              <div className="px-5 py-4 bg-card border-t border-border flex items-center justify-between flex-wrap gap-2">
                <div className="flex items-center gap-3 font-mono text-[0.7rem] uppercase tracking-[0.14em] text-foreground/70">
                  <span>{formatDate(post.date)}</span>
                  <span className="h-1 w-1 rounded-lg bg-primary" />
                  <span>{category}</span>
                </div>
                <span className="font-mono text-[0.7rem] uppercase tracking-[0.14em] text-foreground/70">
                  {estimateReadingTime(post.content, post.excerpt)}
                </span>
              </div>
            </Link>
          </article>;
    })}
    </div>;
}
export default function () {
  const wp_query_params = {
    post_type: "post",
    per_page: 6,
    order: "desc",
    orderby: "date"
  };
  const wp_query = new WP_Query(wp_query_params);
  return <section id="streaminginsightsblog" className="relative bg-background text-foreground py-28 md:py-40 overflow-hidden">
      {}
      <div className="absolute inset-0 opacity-[0.08] pointer-events-none" style={{
      backgroundImage: "radial-gradient(circle at 12% 18%, hsl(var(--foreground)) 0%, transparent 1.5%), radial-gradient(circle at 78% 32%, hsl(var(--foreground)) 0%, transparent 1.2%), radial-gradient(circle at 35% 70%, hsl(var(--foreground)) 0%, transparent 1.8%), radial-gradient(circle at 88% 85%, hsl(var(--foreground)) 0%, transparent 1.4%), radial-gradient(circle at 50% 50%, hsl(var(--foreground)) 0%, transparent 1%)"
    }} />

      <div className="relative max-w-[1320px] mx-auto px-6 md:px-10 lg:px-16">
        {}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12 mb-16 md:mb-24">
          {}
          <div className="lg:col-span-7">
            <p className="font-mono text-[0.75rem] uppercase tracking-[0.18em] text-primary mb-6">
              § V.001 / VPN — LATEST INSIGHTS
            </p>
            <h2 className="font-sans font-normal text-foreground leading-[1.02] tracking-[-0.02em]" style={{
            fontSize: "clamp(2.5rem, 5.4vw, 4.75rem)"
          }}>
              Secure your digital <em className="italic">presence</em> online.
            </h2>
            {}
            <div className="mt-8 flex items-center">
              <span className="h-px w-16 bg-foreground" />
              <span className="ml-2 h-1.5 w-1.5 rounded-lg bg-primary" />
            </div>
          </div>

          {}
          <div className="lg:col-span-5 lg:pt-4">
            <p className="font-sans italic text-foreground/80 leading-[1.55]" style={{
            fontSize: "clamp(0.95rem, 1.1vw, 1.0625rem)"
          }}>
              Expert analysis on cybersecurity, privacy tools, and speed optimization 
              to keep your data safe and your connection fast.
            </p>
            <div className="mt-6 flex flex-wrap gap-2">
              {filterChips.map((chip, i) => <Badge data-index={i} key={chip} variant="outline" className="font-mono text-[0.65rem] uppercase tracking-[0.16em] rounded-md border border-primary text-primary bg-transparent px-2.5 py-1 cursor-pointer hover:bg-primary hover:text-primary-foreground transition-colors">
                  {chip}
                </Badge>)}
            </div>
          </div>
        </div>

        {}
        <PostsProvider wp_query={{
        post_type: "post",
        per_page: 6,
        order: "desc",
        orderby: "date",
        status: ["publish"]
      }}>
          <RecentChaptersGrid />
        </PostsProvider>
      </div>
    </section>;
}